import type { PlanContent, SpecContent } from "@szyos/contracts";
import { api } from "./api";

/** Beispiel-Briefing für den Demo-Auftrag (Mock-Agent, keine echten Kosten). */
export const DEMO_SPEC: SpecContent = {
  problem: "Ältere Android-Geräte laufen voll, weil App-Caches unbemerkt wachsen.",
  audience: "Menschen mit älteren Geräten, keine Technik-Profis",
  mustHaves: [
    "Belegten Speicher je App verständlich anzeigen",
    "Caches nach Bestätigung sicher leeren",
    "Keine persönlichen Daten löschen",
  ],
  nonGoals: ["Keine Werbung", "Kein Tracking"],
  designNotes: "Ruhig, vertrauenswürdig, dunkles Design mit großen Bedienelementen",
  acceptance: ["Löschen nur nach ausdrücklicher Bestätigung", "Funktioniert ab Android 10"],
};

/** Demo-Plan mit Markern für den Mock-Adapter: eine Rückfrage, ein Wackelkandidat. */
export const DEMO_PLAN: PlanContent = {
  items: [
    {
      key: "storage-api",
      lane: "research",
      title: "Storage-API Android 10 bis 15 klären",
      acceptanceCriteria: ["Quellen belegt"],
      dependsOn: [],
      providerProfile: "mock",
    },
    {
      key: "rechte",
      lane: "research",
      title: "Berechtigungsmodell Cache-Zugriff",
      acceptanceCriteria: ["Rechte dokumentiert"],
      dependsOn: [],
      providerProfile: "mock",
    },
    {
      key: "screens",
      lane: "design",
      title: "Startscreen mit Speicherring",
      acceptanceCriteria: ["Entwurf hell und dunkel"],
      dependsOn: [],
      providerProfile: "mock",
    },
    {
      key: "dialog",
      lane: "design",
      title: "Bestätigungsdialog Löschen",
      acceptanceCriteria: ["Barrierefrei bedienbar"],
      dependsOn: ["screens"],
      providerProfile: "mock",
    },
    {
      key: "liste",
      lane: "code",
      title: "App-Liste mit Cache-Größen",
      acceptanceCriteria: ["Liste zeigt Größen"],
      dependsOn: ["storage-api", "screens"],
      providerProfile: "mock",
    },
    {
      key: "loeschen",
      lane: "code",
      title: "Löschvorgang mit Bestätigung [mock:flaky]",
      acceptanceCriteria: ["Nur Caches werden gelöscht"],
      dependsOn: ["liste", "dialog", "rechte"],
      providerProfile: "mock",
    },
    {
      key: "unit",
      lane: "test",
      title: "Unit-Tests Größenberechnung",
      acceptanceCriteria: ["Grenzwerte geprüft"],
      dependsOn: ["liste"],
      providerProfile: "mock",
    },
    {
      key: "geraete",
      lane: "test",
      title: "Gerätetests auf Hardware [mock:question]",
      acceptanceCriteria: ["Android 10 bis 15"],
      dependsOn: ["loeschen"],
      providerProfile: "mock",
    },
  ],
};

/** Legt einen kompletten Demo-Auftrag an und startet ihn (nur Owner). */
export async function createDemoWorkOrder(): Promise<string> {
  const project = await api.createProject({
    title: "spaceBACK",
    goal: "Android-App, die App-Caches sicher leert und belegten Speicher verständlich anzeigt.",
    platform: "android",
    profileKey: "android-kotlin",
  });
  let wo = await api.createWorkOrder(project.id, { title: "MVP v0.1", kind: "new_app", budgetUsd: 5 });
  wo = await api.submitSpec(wo.id, DEMO_SPEC, wo.version);
  wo = await api.approveSpec(wo.id, wo.specHash ?? "", wo.version);
  wo = await api.submitPlan(wo.id, DEMO_PLAN, wo.version);
  wo = await api.approvePlan(wo.id, wo.planHash ?? "", wo.version);
  wo = await api.start(wo.id, wo.version);
  return wo.id;
}
